import { getParameterByName } from './qs';

export const OAUTH_REQUEST_ID_KEY = 'mcp_oauth_request_id';
export const OAUTH_SOURCE_KEY = 'mcp_oauth_source';
/** MCP 企微场景 URL 参数 key（与企微官方参数区分；接口传参仍用 request_id / source） */
export const MCP_WECOM_URL_REQUEST_ID_KEY = 'oauth_request_id';
export const MCP_WECOM_URL_SOURCE_KEY = 'oauth_source';
const MCP_WECOM_URL_PARAMS_MODE_KEY = 'mcp_oauth_wecom_url_params';
export const OAUTH_ACCOUNT_ID_KEY = 'mcp_oauth_account_id';
export const OAUTH_ENV_STORAGE_KEY = 'mcp_oauth_env';
export const OAUTH_ORGS_STORAGE_KEY = 'mcp_oauth_orgs';
export const OAUTH_SUCCESS_STORAGE_KEY = 'mcp_oauth_success';

const LEGACY_OAUTH_STORAGE_KEYS = {
  [OAUTH_REQUEST_ID_KEY]: 'workbuddy_oauth_request_id',
  [OAUTH_SOURCE_KEY]: 'workbuddy_oauth_source',
  [OAUTH_ACCOUNT_ID_KEY]: 'workbuddy_oauth_account_id',
  [OAUTH_ENV_STORAGE_KEY]: 'workbuddy_oauth_env',
  [OAUTH_ORGS_STORAGE_KEY]: 'workbuddy_oauth_orgs',
  [OAUTH_SUCCESS_STORAGE_KEY]: 'workbuddy_oauth_success',
  [MCP_WECOM_URL_PARAMS_MODE_KEY]: 'workbuddy_mcp_wecom_url_params',
};

function migrateLegacyOAuthStorage() {
  Object.entries(LEGACY_OAUTH_STORAGE_KEYS).forEach(([nextKey, legacyKey]) => {
    if (!sessionStorage.getItem(nextKey) && sessionStorage.getItem(legacyKey)) {
      sessionStorage.setItem(nextKey, sessionStorage.getItem(legacyKey));
    }
  });
}

/** POST /oauth2/authorize/confirm 的 consumer_code */
export const OAUTH_CONSUMER_CODE = {
  DULI: 1,
  QIWEI: 2,
  DING: 3,
  OXM: 4,
};

export const OAUTH_ENV_OPTIONS = [
  { value: 'production', label: '生产环境' },
  { value: 'staging', label: 'Staging 环境' },
  { value: 'test', label: 'Test 环境' },
];

/** MCP 环境选择 → UC 域名（老 UC 入口，非 MCP 授权页） */
export const OAUTH_ENV_HOST_MAP = {
  production: 'https://uc.weiwenjia.com',
  staging: 'https://uc-staging.weiwenjia.com',
  test: 'https://uc-test.weiwenjia.com',
};

/**
 * MCP OAuth 专用授权域名（步骤 1 跨环境跳转、全流程 UI 应停留在此域名）。
 * 与 OAUTH_ENV_HOST_MAP（老 UC 入口）分离；MCP 接口域名见各环境 AppConf.mcpOAuthApi.domain（oauth.js 读取）。
 */
export const MCP_AUTH_ENV_HOST_MAP = {
  production: 'https://auth.weiwenjia.com',
  staging: 'https://auth-staging.weiwenjia.com',
  test: 'https://auth-test.weiwenjia.com',
};

export function getOAuthEnvHost(env = getOAuthEnv()) {
  return OAUTH_ENV_HOST_MAP[env] || OAUTH_ENV_HOST_MAP.production;
}

export function getMcpAuthEnvHost(env = getOAuthEnv()) {
  return MCP_AUTH_ENV_HOST_MAP[env] || MCP_AUTH_ENV_HOST_MAP.production;
}

/** 当前页面是否在 MCP 授权专用域名上 */
export function isMcpAuthHostname(hostname = window.location.hostname) {
  return hostname.startsWith('auth-') || hostname === 'auth.weiwenjia.com';
}

/** 当前域名是否为老 UC 入口（非 MCP 授权专用域） */
export function isLegacyUcHostname(hostname = window.location.hostname) {
  return hostname === 'uc.weiwenjia.com'
    || hostname.startsWith('uc-');
}

/**
 * MCP 入口若落在 uc-* 域名，自动切到同环境的 auth-* 域名（保留 path / search / hash）。
 */
export function redirectToMcpAuthDomainIfNeeded() {
  if (typeof window === 'undefined') {
    return false;
  }
  if (isMcpAuthHostname()) {
    return false;
  }
  if (!getRequestIdFromUrl()) {
    return false;
  }
  if (!isLegacyUcHostname()) {
    return false;
  }
  const authOrigin = getMcpAuthEnvHost(getOAuthEnvFromHostname());
  if (window.location.origin === authOrigin) {
    return false;
  }
  window.location.replace(
    `${authOrigin}${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
  return true;
}

/** 根据当前域名推断环境（用于步骤 1 默认选中） */
export function getOAuthEnvFromHostname(hostname = window.location.hostname) {
  if (hostname === 'uc.weiwenjia.com' || hostname === 'auth.weiwenjia.com') {
    return 'production';
  }
  if (
    hostname.includes('uc-staging')
    || hostname.includes('auth-staging')
    || (hostname.includes('staging') && !hostname.includes('test'))
  ) {
    return 'staging';
  }
  if (
    hostname.includes('uc-test')
    || hostname.includes('auth-test')
    || hostname.includes('-test')
  ) {
    return 'test';
  }
  return 'test';
}

/**
 * 构建指定环境下的 MCP 页面完整 URL（跨域跳转时 search + hash 均携带 OAuth 参数）。
 * 同环境且当前已在 MCP 授权域时，优先使用 window.location.origin 避免跳回 uc-*。
 */
export function buildOAuthEnvPageUrl(env, hashPath = MCP_LOGIN_PATH) {
  const currentEnv = typeof window !== 'undefined'
    ? getOAuthEnvFromHostname(window.location.hostname)
    : null;
  let base = getMcpAuthEnvHost(env);
  if (
    typeof window !== 'undefined'
    && env === currentEnv
    && (isMcpAuthHostname() || window.location.hostname.includes('uc-'))
  ) {
    base = window.location.origin;
  }
  const hash = buildOAuthHashPath(hashPath);
  return `${base}/web/index.html${buildOAuthQuery()}#${hash.replace(/^\//, '')}`;
}

/** MCP OAuth 对接专用页面（步骤 2 复用 #/lxyunLogin） */
export const MCP_ROUTE_PATHS = [
  '/mcp-auth',
  '/mcp-select-org',
  '/mcp-success',
  '/mcp-oauth-resume',
];

/** 旧版 WorkBuddy 路由，兼容重定向 */
export const LEGACY_MCP_ROUTE_PATHS = [
  '/workbuddy-auth',
  '/workbuddy-select-org',
  '/workbuddy-success',
  '/workbuddy-oauth-resume',
];

export const LEGACY_MCP_ROUTE_MAP = {
  '/workbuddy-auth': '/mcp-auth',
  '/workbuddy-select-org': '/mcp-select-org',
  '/workbuddy-success': '/mcp-success',
  '/workbuddy-oauth-resume': '/mcp-oauth-resume',
};

export const MCP_LOGIN_PATH = '/lxyunLogin';

export function resolveMcpOAuthRoute(pathname = '') {
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return LEGACY_MCP_ROUTE_MAP[normalized] || normalized;
}

export function isMcpOAuthRoute(pathname = '') {
  const normalized = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return MCP_ROUTE_PATHS.some((path) => normalized.startsWith(path))
    || LEGACY_MCP_ROUTE_PATHS.some((path) => normalized.startsWith(path));
}

function getNormalizedHashPath() {
  const hashPath = (window.location.hash || '').split('?')[0].replace(/^#/, '') || '';
  return hashPath.startsWith('/') ? hashPath : `/${hashPath}`;
}

/** 将旧 #/workbuddy-* hash 重定向到 #/mcp-* */
export function redirectLegacyMcpHashRouteIfNeeded() {
  const hashPath = getNormalizedHashPath();
  const targetPath = LEGACY_MCP_ROUTE_MAP[hashPath];
  if (!targetPath) {
    return false;
  }
  const hash = window.location.hash || '';
  const queryPart = hash.includes('?') ? hash.slice(hash.indexOf('?')) : '';
  window.location.replace(
    `${window.location.pathname}${window.location.search}#${targetPath}${queryPart}`,
  );
  return true;
}

/**
 * 后端入口若只带 search 参数（无 hash），自动进入 #/mcp-auth，避免走普通 UC 路由。
 */
export function redirectToMcpEntryIfNeeded() {
  const requestId = getRequestIdFromUrl();
  if (!requestId) {
    return false;
  }
  const hashPath = getNormalizedHashPath();
  if (!hashPath || hashPath === '/') {
    window.location.replace(
      `${window.location.pathname}${window.location.search}#/mcp-auth`,
    );
    return true;
  }
  const resolved = resolveMcpOAuthRoute(hashPath);
  if (MCP_ROUTE_PATHS.includes(resolved) || hashPath === MCP_LOGIN_PATH) {
    return false;
  }
  return false;
}

function isMcpDedicatedRoute() {
  return MCP_ROUTE_PATHS.includes(resolveMcpOAuthRoute(getNormalizedHashPath()));
}

function isMcpLoginRoute() {
  return getNormalizedHashPath() === MCP_LOGIN_PATH;
}

/** 从 URL search / hash 读取 requestId（企微场景 oauth_request_id；入口兼容 request_id / requestId） */
function getRequestIdFromUrl() {
  return getParameterByName(MCP_WECOM_URL_REQUEST_ID_KEY)
    || getParameterByName(MCP_WECOM_URL_REQUEST_ID_KEY, true)
    || getParameterByName('requestId')
    || getParameterByName('request_id')
    || getParameterByName('requestId', true)
    || getParameterByName('request_id', true);
}

/** 从 URL search / hash 读取 source（企微场景 oauth_source；入口兼容 source） */
function getOAuthSourceFromUrl() {
  return getParameterByName(MCP_WECOM_URL_SOURCE_KEY)
    || getParameterByName(MCP_WECOM_URL_SOURCE_KEY, true)
    || getParameterByName('source')
    || getParameterByName('source', true);
}

export function enableMcpWecomUrlParamsMode() {
  sessionStorage.setItem(MCP_WECOM_URL_PARAMS_MODE_KEY, '1');
}

export function isMcpWecomUrlParamsMode() {
  return sessionStorage.getItem(MCP_WECOM_URL_PARAMS_MODE_KEY) === '1'
    || Boolean(
      getParameterByName(MCP_WECOM_URL_REQUEST_ID_KEY)
      || getParameterByName(MCP_WECOM_URL_REQUEST_ID_KEY, true),
    );
}

function stripLegacyMcpUrlParams(params) {
  ['request_id', 'requestId', 'source'].forEach((key) => params.delete(key));
}

function stripWecomMcpUrlParams(params) {
  params.delete(MCP_WECOM_URL_REQUEST_ID_KEY);
  params.delete(MCP_WECOM_URL_SOURCE_KEY);
}

/**
 * 从 URL 或 sessionStorage 读取 requestId。
 * #/lxyunLogin 仅认 URL 参数（普通登录与 MCP 共用此页，避免 sessionStorage 污染普通登录）。
 */
export function getRequestId() {
  const fromUrl = getRequestIdFromUrl();
  if (fromUrl) {
    return fromUrl;
  }
  if (isMcpDedicatedRoute()) {
    return sessionStorage.getItem(OAUTH_REQUEST_ID_KEY) || '';
  }
  return '';
}

export function getOAuthSource() {
  const fromUrl = getOAuthSourceFromUrl();
  if (fromUrl) {
    return fromUrl;
  }
  if (isMcpDedicatedRoute()) {
    return sessionStorage.getItem(OAUTH_SOURCE_KEY) || '';
  }
  return '';
}

export function hasOAuthContext() {
  return Boolean(getRequestId());
}

/**
 * WorkBuddy MCP 对接：是否隐藏独立版 App 二维码（仅 UI，任意 UC 环境）。
 * 仅依据 URL 参数判断，避免 #/lxyunLogin 首屏 sessionStorage 未就绪时误判。
 */
function isMcpOAuthSource(source = '') {
  if (!source) {
    return false;
  }
  const normalized = source.toLowerCase();
  return normalized === 'mcp' || normalized === 'workbuddy' || normalized === 'qwenwork';
}

export function shouldHideMcpStandaloneAppQr() {
  const source = getOAuthSourceFromUrl();
  return Boolean(getRequestIdFromUrl() || isMcpOAuthSource(source));
}

/**
 * 进入 UC 时持久化 requestId / source。
 * 普通 #/lxyunLogin 且无 MCP 参数时清除残留，避免污染普通登录。
 */
export function initMcpOAuthContext() {
  migrateLegacyOAuthStorage();
  if (redirectLegacyMcpHashRouteIfNeeded()) {
    return;
  }
  if (redirectToMcpAuthDomainIfNeeded()) {
    return;
  }
  if (redirectToMcpEntryIfNeeded()) {
    return;
  }
  const requestId = getRequestIdFromUrl();
  const source = getOAuthSourceFromUrl();
  if (
    getParameterByName(MCP_WECOM_URL_REQUEST_ID_KEY)
    || getParameterByName(MCP_WECOM_URL_REQUEST_ID_KEY, true)
  ) {
    enableMcpWecomUrlParamsMode();
  }
  if (requestId) {
    sessionStorage.setItem(OAUTH_REQUEST_ID_KEY, requestId);
  }
  if (source) {
    sessionStorage.setItem(OAUTH_SOURCE_KEY, source);
  }
  if (!requestId) {
    // 普通 #/lxyunLogin 或非 MCP 专用页：清除残留，避免误走 MCP 登录
    if (isMcpLoginRoute() || !isMcpDedicatedRoute()) {
      clearOAuthContext();
    }
  }
}

export function getOAuthAccountId() {
  return sessionStorage.getItem(OAUTH_ACCOUNT_ID_KEY) || '';
}

export function setOAuthAccountId(accountId) {
  if (accountId !== undefined && accountId !== null && accountId !== '') {
    sessionStorage.setItem(OAUTH_ACCOUNT_ID_KEY, String(accountId));
  }
}

/** 企微扫码回跳 URL 中的 oid / aid（后端已在手机端选定企业） */
export function initWecomOAuthResumeFromUrl() {
  const orgId = getParameterByName('oid') || getParameterByName('oid', true) || '';
  const accountId = getParameterByName('aid') || getParameterByName('aid', true) || '';
  if (accountId) {
    setOAuthAccountId(accountId);
  }
  return { orgId, accountId };
}

/** 同时写入 camelCase / snake_case，兼容前后端不同命名 */
export function setDualKeyParams(target, camelKey, snakeKey, value) {
  if (value !== undefined && value !== null && value !== '') {
    target[camelKey] = value;
    target[snakeKey] = value;
  }
}

/** 调用 UC MCP 相关接口时附带的公共参数（双 key） */
export function getMcpOAuthApiParams() {
  const params = {};
  setDualKeyParams(params, 'requestId', 'request_id', getRequestId());
  const source = getOAuthSource();
  if (source) {
    params.source = source;
  }
  return params;
}

function appendMcpParamsToUrlSearchParams(params) {
  const requestId = getRequestId();
  const source = getOAuthSource();
  if (isMcpWecomUrlParamsMode()) {
    stripLegacyMcpUrlParams(params);
    if (requestId) {
      params.set(MCP_WECOM_URL_REQUEST_ID_KEY, requestId);
    }
    if (source) {
      params.set(MCP_WECOM_URL_SOURCE_KEY, source);
    }
    return;
  }
  stripWecomMcpUrlParams(params);
  if (requestId) {
    params.set('requestId', requestId);
    params.set('request_id', requestId);
  }
  if (source) {
    params.set('source', source);
  }
}

export function getOAuthEnv() {
  return sessionStorage.getItem(OAUTH_ENV_STORAGE_KEY) || 'production';
}

export function setOAuthEnv(env) {
  sessionStorage.setItem(OAUTH_ENV_STORAGE_KEY, env || 'production');
}

export function getOAuthEnvLabel(env = getOAuthEnv()) {
  const item = OAUTH_ENV_OPTIONS.find((option) => option.value === env);
  return item ? item.label : env;
}

export function buildOAuthQuery(extra = {}) {
  const params = new URLSearchParams();
  appendMcpParamsToUrlSearchParams(params);
  Object.keys(extra).forEach((key) => {
    const value = extra[key];
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, value);
    }
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

/** 企微/钉钉回调 successUrl 等场景，在 search 上追加 MCP 参数 */
export function appendMcpOAuthParamsToSearch(search = '') {
  const requestId = getRequestId();
  const source = getOAuthSource();
  if (!requestId && !source) {
    return search;
  }
  const params = new URLSearchParams(search.replace(/^\?/, ''));
  appendMcpParamsToUrlSearchParams(params);
  const query = params.toString();
  return query ? `?${query}` : '';
}

export const MCP_DINGTALK_RETURN_FROM = 'dingding';

/** 钉钉 MCP 扫码回跳选企页（hash / search 带 from=dingding） */
export function isMcpDingTalkOAuthReturn() {
  return getParameterByName('from', true) === MCP_DINGTALK_RETURN_FROM
    || getParameterByName('from') === MCP_DINGTALK_RETURN_FROM;
}

function getOAuthUrlParam(name, aliases = []) {
  const keys = [name, ...aliases];
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    const fromHash = getParameterByName(key, true);
    if (fromHash) {
      return fromHash;
    }
    const fromSearch = getParameterByName(key);
    if (fromSearch) {
      return fromSearch;
    }
  }
  return '';
}

/** 钉钉 MCP 回跳选企页：从 URL 读取 POST /oauth2/ding/login 所需参数 */
export function getMcpDingTalkLoginParamsFromUrl() {
  return {
    code: getOAuthUrlParam('code'),
    suiteKey: getOAuthUrlParam('suiteKey', ['suite_key']),
    requestId: getRequestId() || getOAuthUrlParam('requestId', ['request_id']),
    appToken: getOAuthUrlParam('appToken', ['app_token'])
      || (typeof window !== 'undefined' && window.AppConf?.appToken?.DINGDING)
      || '',
  };
}

/**
 * 钉钉 MCP：传给 Ruby /dingtalk/sign_in 的 oauth_url（扫码完成后回 UC 选企页）。
 */
export function buildMcpDingTalkSelectOrgOAuthUrl({
  origin = window.location.origin,
  pathname = window.location.pathname,
} = {}) {
  const requestId = getRequestId();
  const source = getOAuthSource();

  const searchParams = new URLSearchParams();
  if (requestId) {
    searchParams.set('request_id', requestId);
    searchParams.set('requestId', requestId);
  }
  if (source) {
    searchParams.set('source', source);
  }
  const searchQuery = searchParams.toString();
  const hash = buildOAuthHashPath('/mcp-select-org', { from: MCP_DINGTALK_RETURN_FROM });
  return `${origin}${pathname}${searchQuery ? `?${searchQuery}` : ''}#${hash}`;
}

/**
 * 钉钉 MCP 扫码回跳 successUrl（仍用 request_id / source）。
 */
export function buildMcpOAuthCallbackSuccessUrl({
  origin = window.location.origin,
  pathname = window.location.pathname,
  resumeHash = '#/mcp-oauth-resume',
} = {}) {
  const requestId = getRequestIdFromUrl()
    || sessionStorage.getItem(OAUTH_REQUEST_ID_KEY)
    || '';
  const source = getOAuthSourceFromUrl()
    || sessionStorage.getItem(OAUTH_SOURCE_KEY)
    || '';

  const params = new URLSearchParams(window.location.search.replace(/^\?/, ''));
  stripWecomMcpUrlParams(params);
  if (requestId) {
    params.set('request_id', requestId);
    params.set('requestId', requestId);
  }
  if (source) {
    params.set('source', source);
  }
  const query = params.toString();
  return `${origin}${pathname}${query ? `?${query}` : ''}${resumeHash}`;
}

/**
 * 企微 MCP 扫码回跳 successUrl（oauth_request_id / oauth_source，避免与企微官方参数混淆）。
 */
export function buildMcpOAuthWecomCallbackSuccessUrl({
  origin = window.location.origin,
  pathname = window.location.pathname,
  resumeHash = '#/mcp-oauth-resume',
} = {}) {
  enableMcpWecomUrlParamsMode();
  const requestId = getRequestIdFromUrl()
    || sessionStorage.getItem(OAUTH_REQUEST_ID_KEY)
    || '';
  const source = getOAuthSourceFromUrl()
    || sessionStorage.getItem(OAUTH_SOURCE_KEY)
    || '';

  const params = new URLSearchParams();
  if (requestId) {
    params.set(MCP_WECOM_URL_REQUEST_ID_KEY, requestId);
  }
  if (source) {
    params.set(MCP_WECOM_URL_SOURCE_KEY, source);
  }
  const query = params.toString();
  return `${origin}${pathname}${query ? `?${query}` : ''}${resumeHash}`;
}

export function buildOAuthHashPath(path, extra = {}) {
  return `${path}${buildOAuthQuery(extra)}`;
}

export function resumeOAuthStep(history, step) {
  const routeMap = {
    1: '/mcp-auth',
    2: MCP_LOGIN_PATH,
    3: '/mcp-select-org',
    4: '/mcp-success',
  };
  history.push(buildOAuthHashPath(routeMap[step] || routeMap[1]));
}

export function setOAuthSuccessInfo(info = {}) {
  sessionStorage.setItem(OAUTH_SUCCESS_STORAGE_KEY, JSON.stringify(info));
}

export function getOAuthSuccessInfo() {
  try {
    return JSON.parse(sessionStorage.getItem(OAUTH_SUCCESS_STORAGE_KEY) || '{}');
  } catch (error) {
    return {};
  }
}

export function getStoredOAuthOrgs() {
  try {
    return JSON.parse(sessionStorage.getItem(OAUTH_ORGS_STORAGE_KEY) || '[]');
  } catch (error) {
    return [];
  }
}

export function setStoredOAuthOrgs(orgs = []) {
  sessionStorage.setItem(OAUTH_ORGS_STORAGE_KEY, JSON.stringify(orgs));
}

export function clearOAuthContext() {
  sessionStorage.removeItem(OAUTH_REQUEST_ID_KEY);
  sessionStorage.removeItem(OAUTH_SOURCE_KEY);
  sessionStorage.removeItem(OAUTH_ACCOUNT_ID_KEY);
  sessionStorage.removeItem(OAUTH_ENV_STORAGE_KEY);
  sessionStorage.removeItem(OAUTH_ORGS_STORAGE_KEY);
  sessionStorage.removeItem(OAUTH_SUCCESS_STORAGE_KEY);
  sessionStorage.removeItem(MCP_WECOM_URL_PARAMS_MODE_KEY);
}

export function isMcpOAuthLoginRoute(pathname = '') {
  return pathname === MCP_LOGIN_PATH && hasOAuthContext();
}
